import {
  isServer,
  keepPreviousData,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import type { FC, PropsWithChildren } from "react"
import { Suspense } from "react"

import { Toaster } from "@/components/ui/toaster"
import {
  AuthContextProvider,
  useAuth,
} from "@/contexts/auth"
import { PermissionContextProvider } from "@/contexts/permission"
import { WhitelabelContextProvider } from "@/contexts/whitelabel"
import { ErrorHelper } from "@/utils"
const makeQueryClient = (authContext: AuthContextProps) => {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const skipErrorNotification =
          query.meta?.skipErrorNotification
        const errorRecord = error as unknown as Record<
          string,
          unknown
        >
        const isCanceledRequest =
          errorRecord.statusCode === "ERR_CANCELED" ||
          errorRecord.code === "ERR_CANCELED" ||
          error.message?.toLowerCase().includes("canceled")
        if (!skipErrorNotification && !isCanceledRequest) {
          void ErrorHelper.handleError({
            authContext,
            error,
          })
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        retry: false,
      },
    },
  })
}
let browserQueryClient: QueryClient | undefined = undefined
const getQueryClient = (authContext: AuthContextProps) => {
  if (isServer) {
    return makeQueryClient(authContext)
  } else {
    if (!browserQueryClient)
      browserQueryClient = makeQueryClient(authContext)
    return browserQueryClient
  }
}
export const getBrowserQueryClient = () =>
  browserQueryClient
type ProvidersProps = PropsWithChildren
const InnerProviders: FC<ProvidersProps> = ({
  children,
}) => {
  const authContext = useAuth()
  const queryClient = getQueryClient(authContext)
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools
        initialIsOpen={false}
        buttonPosition="bottom-left"
      />
      <WhitelabelContextProvider>
        <PermissionContextProvider>
          {children}
        </PermissionContextProvider>
      </WhitelabelContextProvider>
    </QueryClientProvider>
  )
}
const Providers: FC<ProvidersProps> = ({ children }) => (
  <>
    <AuthContextProvider>
      <Suspense fallback={<div>Carregando...</div>}>
        <InnerProviders>{children}</InnerProviders>
      </Suspense>
    </AuthContextProvider>
    <Toaster />
  </>
)
export default Providers
